import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { getUserDetails } from "../../../redux/userRelated/userHandle";
import { underStudentControl } from "../../../redux/studentRelated/studentSlice";

import {
    Alert,
    alpha,
    Avatar,
    Box,
    Button,
    Chip,
    Container,
    Fade,
    Paper,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import Popup from "../../../components/Popup";

const StudentAttendance = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const params = useParams();

    const { userDetails, loading: userLoading, currentUser } = useSelector((state) => state.user);
    const isTeacher = currentUser?.role === "Teacher";

    const [studentID, setStudentID] = useState("");
    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");

    useEffect(() => {
        dispatch(underStudentControl());
    }, [dispatch]);

    useEffect(() => {
        const stdID = params.id || params.studentID;
        if (stdID) {
            setStudentID(stdID);
            dispatch(getUserDetails(stdID, "Student"));
        }
    }, [params.id, params.studentID, dispatch]);

    const isAdminView = !isTeacher;
    const studentRecord = Array.isArray(userDetails) ? null : userDetails;

    const attendanceRows = useMemo(() => {
        const list = studentRecord?.attendance;
        if (!Array.isArray(list)) return [];
        return [...list].sort((a, b) => new Date(b.date) - new Date(a.date));
    }, [studentRecord]);

    const loading = userLoading && !studentRecord?.name;
    const classLabel = studentRecord?.sclassName?.sclassName || "—";
    const backPath = isTeacher
        ? `/Teacher/class/student/${studentID}`
        : `/Admin/students/student/${studentID}`;

    const openTeacherEntry = () => {
        setMessage("Use Record attendance in the sidebar to submit class attendance for admin review.");
        setShowPopup(true);
    };

    return (
        <Box
            component="main"
            sx={{
                minHeight: "100%",
                pb: 6,
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.success.main, 0.07)} 0%, ${t.palette.grey[50]} 28%)`
                        : alpha(t.palette.common.black, 0.2),
            }}
        >
            <Container maxWidth="sm" sx={{ pt: { xs: 2, sm: 3 } }}>
                {loading ? (
                    <Stack spacing={2}>
                        <Skeleton variant="rounded" height={120} sx={{ borderRadius: 3 }} />
                        <Skeleton variant="rounded" height={280} sx={{ borderRadius: 2 }} />
                    </Stack>
                ) : !studentRecord?.name ? (
                    <Alert severity="warning" sx={{ borderRadius: 2 }}>
                        Could not load this student. Go back and try again.
                    </Alert>
                ) : (
                    <Fade in timeout={360}>
                        <Stack spacing={3}>
                            <Button
                                startIcon={<ArrowBackIosNewOutlinedIcon sx={{ fontSize: 14 }} />}
                                onClick={() => navigate(backPath)}
                                sx={{ alignSelf: "flex-start", fontWeight: 600 }}
                            >
                                Back to student
                            </Button>

                            <Paper
                                elevation={0}
                                sx={{
                                    borderRadius: 3,
                                    overflow: "hidden",
                                    border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                                    boxShadow: (t) => `0 8px 32px ${alpha(t.palette.common.black, 0.06)}`,
                                }}
                            >
                                <Box
                                    sx={{
                                        px: { xs: 2.5, sm: 3 },
                                        py: 2.5,
                                        background: (t) =>
                                            `linear-gradient(115deg, ${t.palette.success.dark} 0%, ${t.palette.success.main} 50%, ${alpha(
                                                t.palette.success.light,
                                                0.92
                                            )} 100%)`,
                                        color: "common.white",
                                    }}
                                >
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Avatar
                                            sx={{
                                                width: 56,
                                                height: 56,
                                                bgcolor: alpha("#fff", 0.2),
                                                color: "inherit",
                                            }}
                                        >
                                            <FactCheckOutlinedIcon />
                                        </Avatar>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="overline" sx={{ opacity: 0.92, letterSpacing: 1.1 }}>
                                                {isAdminView ? "Attendance (read-only)" : "Attendance"}
                                            </Typography>
                                            <Typography variant="h5" fontWeight={800} noWrap title={studentRecord.name}>
                                                {studentRecord.name}
                                            </Typography>
                                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                                                <Chip
                                                    size="small"
                                                    label={`Class ${classLabel}`}
                                                    sx={{
                                                        bgcolor: alpha("#fff", 0.18),
                                                        color: "inherit",
                                                        fontWeight: 600,
                                                        border: "1px solid",
                                                        borderColor: alpha("#fff", 0.35),
                                                    }}
                                                />
                                                {studentRecord.rollNum != null && studentRecord.rollNum !== "" ? (
                                                    <Chip
                                                        size="small"
                                                        label={`Roll ${studentRecord.rollNum}`}
                                                        sx={{
                                                            bgcolor: alpha("#fff", 0.18),
                                                            color: "inherit",
                                                            fontWeight: 600,
                                                            border: "1px solid",
                                                            borderColor: alpha("#fff", 0.35),
                                                        }}
                                                    />
                                                ) : null}
                                            </Stack>
                                        </Box>
                                    </Stack>
                                </Box>

                                <Box sx={{ p: { xs: 2, sm: 3 } }}>
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        {isTeacher
                                            ? "Record class attendance from Record attendance in the sidebar, then submit for admin approval."
                                            : "Teachers record attendance by class; approve submissions under Attendance → Attendance review."}
                                    </Alert>
                                    {attendanceRows.length > 0 ? (
                                        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                                                        <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
                                                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {attendanceRows.map((row, idx) => (
                                                        <TableRow key={row._id || row.id || idx}>
                                                            <TableCell>
                                                                {row.date ? new Date(row.date).toLocaleDateString() : "—"}
                                                            </TableCell>
                                                            <TableCell>
                                                                {row.subName?.subName || row.subName || "—"}
                                                            </TableCell>
                                                            <TableCell>{row.status}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    ) : (
                                        <Alert severity="warning" sx={{ mb: 2 }}>
                                            No attendance records yet.
                                        </Alert>
                                    )}
                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap="wrap">
                                        {isAdminView ? (
                                            <>
                                                <Button
                                                    variant="contained"
                                                    startIcon={<ArticleOutlinedIcon />}
                                                    onClick={() =>
                                                        navigate(`/Admin/students/report-card/${studentID}`)
                                                    }
                                                >
                                                    Report card
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<FactCheckOutlinedIcon />}
                                                    onClick={() => navigate("/Admin/attendance/review")}
                                                >
                                                    Review attendance
                                                </Button>
                                            </>
                                        ) : (
                                            <Button variant="contained" onClick={openTeacherEntry}>
                                                Record class attendance
                                            </Button>
                                        )}
                                        <Button variant="text" onClick={() => navigate(backPath)} sx={{ borderRadius: 2 }}>
                                            Back to student
                                        </Button>
                                    </Stack>
                                </Box>
                            </Paper>
                        </Stack>
                    </Fade>
                )}
            </Container>
            <Popup message={message} setShowPopup={setShowPopup} showPopup={showPopup} />
        </Box>
    );
};

export default StudentAttendance;
